import React, { useEffect, useMemo, useState } from 'react';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import { Button } from '@ui';
import {
  PUBLIC_REPORTS_PAGE_SIZE,
  feedbackPhotoUrl,
  listFeedback,
  listServiceRequests,
  serviceRequestPhotoUrl,
} from '../../hookServices/feedbackApi';
import styles from './StationPublicReports.module.css';

const PHOTO_KIND_ORDER = ['car', 'car_port', 'station'];

const MONTH_KEYS = [
  'january',
  'february',
  'march',
  'april',
  'may',
  'june',
  'july',
  'august',
  'september',
  'october',
  'november',
  'december',
];

function formatWhen(value, t) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const month = t(`monthsShort.${MONTH_KEYS[date.getMonth()]}`);
  return `${date.getDate()} ${month} ${date.getFullYear()}`;
}

function Stars({ rating }) {
  const value = Math.max(0, Math.min(5, Number(rating) || 0));
  return (
    <span className={styles.stars} aria-hidden="true">
      <span>{'★★★★★'.slice(0, value)}</span>
      <span className={styles.starsEmpty}>{'★★★★★'.slice(value)}</span>
    </span>
  );
}

function formatScore(value, language) {
  const text = Number(value).toFixed(1);
  return language === 'en' ? text : text.replace('.', ',');
}

function ReviewOverview({ avg, total, histogram }) {
  const { t, i18n } = useTranslation();
  const hasHistogram = Array.isArray(histogram) && histogram.length > 0;
  const counts = new Map((histogram || []).map(row => [Number(row.rating), Number(row.count) || 0]));
  const maxCount = Math.max(1, ...[1, 2, 3, 4, 5].map(star => counts.get(star) || 0));
  return (
    <div className={styles.overview} aria-label={t('feedback.avgAria', { rating: formatScore(avg, i18n.language) })}>
      <div className={styles.overviewScore}>
        <p className={styles.overviewValue}>{formatScore(avg, i18n.language)}</p>
        <AvgStars value={avg} label={t('feedback.avgAria', { rating: formatScore(avg, i18n.language) })} />
        <p className={styles.overviewCount}>{t('feedback.reviewsCount', { count: total })}</p>
      </div>
      {hasHistogram ? (
        <div className={styles.overviewBars}>
          {[5, 4, 3, 2, 1].map(star => {
            const count = counts.get(star) || 0;
            const width = count === 0 ? '0%' : `max(6px, ${(count / maxCount) * 100}%)`;
            return (
              <div key={star} className={styles.overviewRow}>
                <span className={styles.overviewStar}>{star}</span>
                <span className={styles.overviewTrack}>
                  <span className={styles.overviewFill} style={{ width }} />
                </span>
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

function AvgStars({ value, label }) {
  const rating = Math.max(0, Math.min(5, Number(value) || 0));
  return (
    <span className={styles.avgStars} aria-label={label}>
      <span className={styles.avgStarsBase} aria-hidden="true">
        ★★★★★
      </span>
      <span className={styles.avgStarsFill} style={{ width: `${(rating / 5) * 100}%` }} aria-hidden="true">
        <span>★★★★★</span>
      </span>
    </span>
  );
}

function Pager({ page, total, onChange, prevLabel, nextLabel, pageLabel }) {
  const pages = Math.max(1, Math.ceil(total / PUBLIC_REPORTS_PAGE_SIZE));
  if (pages <= 1) return null;
  return (
    <div className={styles.pager}>
      <button type="button" disabled={page <= 1} onClick={() => onChange(page - 1)}>
        {prevLabel}
      </button>
      <span>{pageLabel}</span>
      <button type="button" disabled={page >= pages} onClick={() => onChange(page + 1)}>
        {nextLabel}
      </button>
    </div>
  );
}

function PhotoButton({ src, alt, onOpen }) {
  return (
    <button type="button" className={styles.photoBtn} onClick={onOpen}>
      <img src={src} alt={alt} />
    </button>
  );
}

function buildGallery(items, isFeedback, t) {
  const photos = [];
  (items || []).forEach(item => {
    if (isFeedback) {
      if (item.has_photo) {
        photos.push({ src: feedbackPhotoUrl(item.id), alt: t('feedback.carPhotoLabel') });
      }
      return;
    }
    PHOTO_KIND_ORDER.filter(photoKind => (item.photo_kinds || []).includes(photoKind)).forEach(photoKind => {
      photos.push({
        src: serviceRequestPhotoUrl(item.id, photoKind),
        alt: t(`serviceRequestForm.photoSlots.${photoKind}`),
      });
    });
  });
  return photos;
}

export default function StationPublicReports({ kind, stationNumbers, reloadToken = 0, onCompose, className }) {
  const { t } = useTranslation();
  const stationKey = useMemo(
    () =>
      (stationNumbers || [])
        .map(value => String(value ?? '').trim())
        .filter(Boolean)
        .join(','),
    [stationNumbers]
  );
  const stations = useMemo(() => (stationKey ? stationKey.split(',') : []), [stationKey]);
  const showStation = stations.length > 1;
  const isFeedback = kind === 'feedback';

  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [histogram, setHistogram] = useState(null);
  const [viewerIndex, setViewerIndex] = useState(null);

  useEffect(() => {
    setPage(1);
    setOpen(false);
  }, [stationKey]);

  useEffect(() => {
    if (!stations.length) {
      setData({ items: [], total: 0, page: 1, avg_rating: null });
      return undefined;
    }
    let cancelled = false;
    const load = isFeedback ? listFeedback : listServiceRequests;
    load({ stationNumbers: stations, page })
      .then(next => {
        if (!cancelled) setData(next);
      })
      .catch(() => {
        if (!cancelled) setData({ items: [], total: 0, page, avg_rating: null });
      });
    return () => {
      cancelled = true;
    };
  }, [stationKey, stations, page, reloadToken, isFeedback]);

  useEffect(() => {
    if (!isFeedback || !data) {
      setHistogram(null);
      return undefined;
    }
    if (Array.isArray(data.rating_histogram) && data.rating_histogram.length > 0) {
      setHistogram(data.rating_histogram);
      return undefined;
    }
    const totalCount = Number(data.total) || 0;
    if (totalCount <= 0 || totalCount > 60) {
      setHistogram(null);
      return undefined;
    }
    let cancelled = false;
    const pages = Math.ceil(totalCount / PUBLIC_REPORTS_PAGE_SIZE);
    Promise.all(
      Array.from({ length: pages }, (_, index) => listFeedback({ stationNumbers: stations, page: index + 1 }))
    )
      .then(lists => {
        if (cancelled) return;
        const counts = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
        lists.forEach(list => {
          (list.items || []).forEach(item => {
            const rating = Number(item.rating);
            if (counts[rating] != null) counts[rating] += 1;
          });
        });
        setHistogram([1, 2, 3, 4, 5].map(rating => ({ rating, count: counts[rating] })));
      })
      .catch(() => {
        if (!cancelled) setHistogram(null);
      });
    return () => {
      cancelled = true;
    };
  }, [isFeedback, data, stations]);

  const gallery = useMemo(() => buildGallery(data?.items, isFeedback, t), [data, isFeedback, t]);
  const viewer = viewerIndex != null ? gallery[viewerIndex] : null;

  useEffect(() => {
    if (viewerIndex == null) return undefined;
    if (viewerIndex >= gallery.length) {
      setViewerIndex(null);
      return undefined;
    }
    const onKey = event => {
      if (event.key === 'Escape') setViewerIndex(null);
      if (event.key === 'ArrowLeft') setViewerIndex(index => (index > 0 ? index - 1 : index));
      if (event.key === 'ArrowRight') {
        setViewerIndex(index => (index != null && index < gallery.length - 1 ? index + 1 : index));
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [viewerIndex, gallery.length]);

  const total = data?.total ?? null;
  const avg = isFeedback && total > 0 && data?.avg_rating != null ? Number(data.avg_rating) : null;
  const label = isFeedback ? t('feedback.leave') : t('serviceRequest');
  const pages = Math.max(1, Math.ceil((total || 0) / PUBLIC_REPORTS_PAGE_SIZE));

  return (
    <section className={cn(styles.root, className)}>
      <Button
        type="button"
        variant="outline"
        className={cn(styles.header, open && styles.headerOpen)}
        style={{
          width: '100%',
          maxWidth: '100%',
          margin: 0,
          ...(open ? { borderBottomLeftRadius: 0, borderBottomRightRadius: 0 } : null),
        }}
        aria-expanded={open}
        onClick={() => setOpen(value => !value)}
      >
        <span className={styles.headerMain}>
          <span className={styles.titleRow}>
            <span className={styles.headerLabel}>{label}</span>
            {!isFeedback && total > 0 ? <span className={styles.count}>{total}</span> : null}
          </span>
          {avg != null ? <AvgStars value={avg} label={t('feedback.avgAria', { rating: avg.toFixed(1) })} /> : null}
        </span>
        <span className={cn(styles.chevron, open && styles.chevronOpen)} aria-hidden="true" />
      </Button>

      {open ? (
        <div className={styles.panel}>
          {isFeedback && avg != null ? <ReviewOverview avg={avg} total={total} histogram={histogram} /> : null}
          {total > 0 ? (
            <ul className={styles.list}>
              {(data.items || []).map(item => {
                if (isFeedback) {
                  return (
                    <li key={item.id} className={styles.card}>
                      <div className={styles.meta}>
                        <Stars rating={item.rating} />
                        <span>{formatWhen(item.created_at, t)}</span>
                        {item.show_port && item.station_number ? <span>#{item.station_number}</span> : null}
                        {item.car_max_kw ? <span>{t('feedback.carPower', { kw: item.car_max_kw })}</span> : null}
                      </div>
                      <p className={styles.text}>{item.text}</p>
                      {item.team_reply ? (
                        <div className={styles.teamReply}>
                          <div className={styles.teamReplyLabel}>{t('feedback.teamLabel')}</div>
                          <p className={styles.teamReplyText}>{item.team_reply}</p>
                        </div>
                      ) : null}
                      {item.has_photo ? (
                        <div className={styles.photos}>
                          <PhotoButton
                            src={feedbackPhotoUrl(item.id, { thumb: true })}
                            alt={t('feedback.carPhotoLabel')}
                            onOpen={() => {
                              const index = gallery.findIndex(photo => photo.src === feedbackPhotoUrl(item.id));
                              if (index >= 0) setViewerIndex(index);
                            }}
                          />
                        </div>
                      ) : null}
                    </li>
                  );
                }
                const kinds = PHOTO_KIND_ORDER.filter(photoKind => (item.photo_kinds || []).includes(photoKind));
                return (
                  <li key={item.id} className={styles.card}>
                    <div className={styles.meta}>
                      <span>{formatWhen(item.created_at, t)}</span>
                      {showStation ? <span>#{item.station_number}</span> : null}
                    </div>
                    {(item.problem_types || []).length > 0 ? (
                      <p className={styles.types}>
                        {item.problem_types
                          .map(type => t(`serviceRequestForm.problemTypes.${type}`, { defaultValue: type }))
                          .join(' · ')}
                      </p>
                    ) : null}
                    <p className={styles.text}>{item.description}</p>
                    {item.team_reply ? (
                      <div className={styles.teamReply}>
                        <div className={styles.teamReplyLabel}>{t('feedback.teamLabel')}</div>
                        <p className={styles.teamReplyText}>{item.team_reply}</p>
                      </div>
                    ) : null}
                    {kinds.length > 0 ? (
                      <div className={styles.photos}>
                        {kinds.map(photoKind => (
                          <PhotoButton
                            key={photoKind}
                            src={serviceRequestPhotoUrl(item.id, photoKind, { thumb: true })}
                            alt={t(`serviceRequestForm.photoSlots.${photoKind}`)}
                            onOpen={() => {
                              const fullSrc = serviceRequestPhotoUrl(item.id, photoKind);
                              const index = gallery.findIndex(photo => photo.src === fullSrc);
                              if (index >= 0) setViewerIndex(index);
                            }}
                          />
                        ))}
                      </div>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className={styles.empty}>{isFeedback ? t('feedback.empty') : t('serviceRequestForm.empty')}</p>
          )}
          {total > PUBLIC_REPORTS_PAGE_SIZE ? (
            <Pager
              page={page}
              total={total}
              onChange={setPage}
              prevLabel={t('feedback.prev')}
              nextLabel={t('feedback.next')}
              pageLabel={t('feedback.pageOf', { page, pages })}
            />
          ) : null}
          {onCompose ? (
            <Button
              type="button"
              variant="outline"
              className={styles.compose}
              style={{ width: '100%', maxWidth: '100%', margin: 0 }}
              onClick={onCompose}
            >
              <span className={styles.composeLabel}>
                {isFeedback ? t('feedback.compose') : t('serviceRequestForm.compose')}
              </span>
            </Button>
          ) : null}
        </div>
      ) : null}

      {viewer ? (
        <div className={styles.lightbox} role="dialog" aria-modal="true" onClick={() => setViewerIndex(null)}>
          {viewerIndex > 0 ? (
            <button
              type="button"
              className={cn(styles.lightboxNav, styles.lightboxPrev)}
              aria-label={t('feedback.prev')}
              onClick={event => {
                event.stopPropagation();
                setViewerIndex(viewerIndex - 1);
              }}
            >
              <span className={styles.lightboxChevron} />
            </button>
          ) : null}
          <img src={viewer.src} alt={viewer.alt || ''} />
          {viewerIndex < gallery.length - 1 ? (
            <button
              type="button"
              className={cn(styles.lightboxNav, styles.lightboxNext)}
              aria-label={t('feedback.next')}
              onClick={event => {
                event.stopPropagation();
                setViewerIndex(viewerIndex + 1);
              }}
            >
              <span className={cn(styles.lightboxChevron, styles.lightboxChevronNext)} />
            </button>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

export function StationPublicReportsSection({ stationNumbers, reloadToken = 0, onFeedback, onService, className }) {
  return (
    <section className={cn(styles.section, className)}>
      <StationPublicReports
        kind="feedback"
        stationNumbers={stationNumbers}
        reloadToken={reloadToken}
        onCompose={onFeedback}
      />
      <StationPublicReports
        kind="service"
        stationNumbers={stationNumbers}
        reloadToken={reloadToken}
        onCompose={onService}
      />
    </section>
  );
}
